import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface RefreshContextType {
  refreshTimestamp: number;
  triggerRefresh: (scope?: RefreshScope) => void;
  registerRefreshHandler: (scope: RefreshScope, handler: () => void) => () => void;
}

// Define different scopes for refreshing different parts of the application
export type RefreshScope = 'posts' | 'profile' | 'comments' | 'likes' | 'all';

const RefreshContext = createContext<RefreshContextType | undefined>(undefined);

export function RefreshProvider({ children }: { children: ReactNode }) {
  const [refreshTimestamp, setRefreshTimestamp] = useState<number>(Date.now());
  const [handlers, setHandlers] = useState<Map<RefreshScope, Set<() => void>>>(new Map());

  // Register a refresh handler for a specific scope
  const registerRefreshHandler = useCallback((scope: RefreshScope, handler: () => void) => {
    setHandlers(prevHandlers => {
      const newHandlers = new Map(prevHandlers);
      if (!newHandlers.has(scope)) {
        newHandlers.set(scope, new Set());
      }
      newHandlers.get(scope)?.add(handler);
      return newHandlers;
    });

    // Return a function to unregister the handler
    return () => {
      setHandlers(prevHandlers => {
        const newHandlers = new Map(prevHandlers);
        newHandlers.get(scope)?.delete(handler);
        if (newHandlers.get(scope)?.size === 0) {
          newHandlers.delete(scope);
        }
        return newHandlers;
      });
    };
  }, []);

  // Trigger a refresh for a specific scope or all scopes
  const triggerRefresh = useCallback((scope: RefreshScope = 'all') => {
    setRefreshTimestamp(Date.now());
    
    // Execute all handlers for the specified scope
    if (scope === 'all') {
      // Execute all handlers for all scopes
      handlers.forEach(scopeHandlers => {
        scopeHandlers.forEach(handler => handler());
      });
    } else {
      // Execute handlers only for the specified scope
      handlers.get(scope)?.forEach(handler => handler());
    }
  }, [handlers]);

  return (
    <RefreshContext.Provider
      value={{
        refreshTimestamp,
        triggerRefresh,
        registerRefreshHandler
      }}
    >
      {children}
    </RefreshContext.Provider>
  );
}

export function useRefresh() {
  const context = useContext(RefreshContext);
  if (context === undefined) {
    throw new Error('useRefresh must be used within a RefreshProvider');
  }
  return context;
}